import React, {Component, createRef} from "react";
import {Document, pdfjs, Page} from "react-pdf";
import {Fade} from "react-bootstrap";
import {TransformWrapper, TransformComponent} from "react-zoom-pan-pinch";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

// pdf.js 5.x wymaga Promise.withResolvers (Chrome 119+, Safari 17.4+)
if (typeof Promise.withResolvers !== "function") {
    Promise.withResolvers = function withResolvers() {
        let resolve;
        let reject;
        const promise = new Promise((res, rej) => {
            resolve = res;
            reject = rej;
        });
        return {promise, resolve, reject};
    };
}

// w testach react-pdf bywa mockowany pustym obiektem - stad opcjonalne wywolania
if (pdfjs?.GlobalWorkerOptions) {
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
        "pdfjs-dist/build/pdf.worker.min.mjs",
        import.meta.url
    ).toString();
}

const PDFJS_CDN = `https://unpkg.com/pdfjs-dist@${pdfjs?.version}`;

const DOCUMENT_OPTIONS = {
    cMapUrl: `${PDFJS_CDN}/cmaps/`,
    standardFontDataUrl: `${PDFJS_CDN}/standard_fonts/`,
    wasmUrl: `${PDFJS_CDN}/wasm/`,
};

const MIN_SCALE = 1;
const MAX_SCALE = 8;
// Canvas renderujemy raz, w podwyzszonej rozdzielczosci. Kazda pozniejsza zmiana
// devicePixelRatio czysci canvas i rysuje go asynchronicznie (biale miganie przy zoomie),
// a rownolegly render tej samej strony na drugim canvasie anuluje pdf.js.
const RENDER_PIXEL_RATIO = Math.min(3, (window.devicePixelRatio || 1) * 2);

// wysrodkowanie zawartosci robi CSS, nie transform - inaczej widac przeskok
// z lewej krawedzi po wyrenderowaniu pierwszej strony
const CONTENT_STYLE = {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
};

class FilePdfShow extends Component {
    state = {
        show: false,
        maxPages: 0,
    };

    transformRef = createRef();

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (this.props.url !== prevProps.url) {
            this.setState({
                show: false,
            });
            this.transformRef.current?.resetTransform(0);
        }
        if (this.props.zoom !== prevProps.zoom) {
            this.applyZoom(this.props.zoom);
        }
    }

    clampScale(scale) {
        if (!Number.isFinite(scale)) return MIN_SCALE;
        return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
    }

    getTransformNodes() {
        const instance = this.transformRef.current?.instance;
        if (!instance) return null;
        const {wrapperComponent, contentComponent, transformState} = instance;
        if (!wrapperComponent || !contentComponent) return null;
        return {wrapperComponent, contentComponent, transformState};
    }

    // zoom sterowany przyciskami z FileShow - wzgledem srodka widocznego obszaru,
    // zeby przy wielostronicowym pliku nie przeskakiwac na srodek dokumentu
    applyZoom(zoom) {
        const api = this.transformRef.current;
        const nodes = this.getTransformNodes();
        if (!api || !nodes) return;
        const {wrapperComponent, transformState} = nodes;
        const scale = this.clampScale(zoom);
        const {scale: current, positionX, positionY} = transformState;
        if (Math.abs(current - scale) < 0.001) return;
        const ratio = scale / current;
        const centerX = wrapperComponent.offsetWidth / 2;
        const centerY = wrapperComponent.offsetHeight / 2;
        api.setTransform(
            centerX - (centerX - positionX) * ratio,
            centerY - (centerY - positionY) * ratio,
            scale,
            200
        );
    }

    onZoomStop = (ref) => {
        this.props.onZoomChange?.(this.clampScale(ref.state.scale));
    };

    onLoadSuccess = (pdf) => {
        this.props.onLoadFile();
        this.setState({
            show: true,
            maxPages: pdf.numPages,
        });
    };

    onLoadError = (error) => {
        console.error(error);
        this.props.onLoadFile();
        this.setState({
            show: true,
        });
    };

    renderPages() {
        const {maxPages} = this.state;
        let array = [];
        for (let i = 0; i < maxPages; i++) {
            array.push(
                <Page
                    devicePixelRatio={RENDER_PIXEL_RATIO}
                    loading={null}
                    pageNumber={i + 1}
                    key={i}
                />
            );
        }
        return array;
    }

    render() {
        const {url} = this.props;
        const {show} = this.state;
        if (!url) return null;
        return (
            <Fade in={show}>
                <div className="file-pdf-show">
                    <TransformWrapper
                        ref={this.transformRef}
                        initialScale={1}
                        minScale={MIN_SCALE}
                        maxScale={MAX_SCALE}
                        limitToBounds={false}
                        doubleClick={{mode: "reset"}}
                        wheel={{step: 0.15}}
                        onZoomStop={this.onZoomStop}>
                        <TransformComponent
                            wrapperClass="file-pdf-show-viewport"
                            contentClass="file-pdf-show-content"
                            contentStyle={CONTENT_STYLE}>
                            <Document
                                file={url}
                                loading={null}
                                onLoadSuccess={this.onLoadSuccess}
                                onLoadError={this.onLoadError}
                                options={DOCUMENT_OPTIONS}>
                                {this.renderPages()}
                            </Document>
                        </TransformComponent>
                    </TransformWrapper>
                </div>
            </Fade>
        );
    }
}

export default FilePdfShow;
